import api from './apiService';
import documentationService from './documentationService';

const authService = {
  // Login user
  login: async (username, password) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`Attempting to login user: ${username} to API endpoint: /auth`);
    }
    try {
      const response = await api.post('/auth', { username, password });
      if (process.env.NODE_ENV === 'development') {
        console.log('Login response received:', response.status);
        console.log('Login response data:', response.data);
      }

      // Parse the response data - the API now returns data in consistent wrapper format
      let responseData = response.data;

      // Check if the response has a body field with JSON string (legacy format)
      if (responseData.body && typeof responseData.body === 'string') {
        try {
          responseData = JSON.parse(responseData.body);
          if (process.env.NODE_ENV === 'development') {
            console.log('Parsed response body:', responseData);
          }
        } catch (parseError) {
          console.error('Error parsing response body JSON:', parseError);
          throw new Error('Invalid response format from server');
        }
      }

      // Handle consistent wrapper format
      if (responseData && responseData.success !== undefined) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Received consistent wrapper format:', responseData);
        }

        // Check if the request was successful
        if (!responseData.success) {
          console.error('Login failed with message:', responseData.message);
          throw new Error(responseData.message || 'Authentication failed');
        }

        // Extract the actual auth data from the data array
        if (responseData.data && responseData.data.length > 0) {
          responseData = responseData.data[0]; // Get the first (and only) item from data array
          if (process.env.NODE_ENV === 'development') {
            console.log('Extracted auth data from wrapper:', responseData);
          }
        } else {
          console.error('No auth data found in successful response');
          throw new Error('No authentication data received');
        }
      }

      // Check if the response indicates an error (legacy format check)
      if (responseData && responseData.message && !responseData.token) {
        // This looks like an error response
        console.error('Login failed with message:', responseData.message);
        throw new Error(responseData.message);
      }

      // Extract user info from the JWT token if possible
      if (responseData && responseData.token) {
        try {
          const token = responseData.token;
          const tokenData = JSON.parse(atob(token.split('.')[1]));

          // Add user info to the response if not already present
          if (!responseData.user) {
            responseData.user = {
              user_id: tokenData.user_id || 'unknown',
              username: tokenData.username || username,
              email: tokenData.email,
              name: tokenData.name,
              role: tokenData.role || 'CONSUMER',
              access_tags: tokenData.access_tags || {}
            };
          }
        } catch (error) {
          console.error('Error extracting user info from token:', error);
          // Provide a minimal user object if we can't extract from token
          if (!responseData.user) {
            responseData.user = {
              user_id: 'unknown',
              username: username,
              role: 'CONSUMER'
            };
          }
        }
      } else {
        // No token in response at all
        console.error('Login response does not contain a token');
        throw new Error('Authentication failed - no token received');
      }

      // Schedule documentation preloading after token is stored in sessionStorage
      authService.scheduleDocumentationPreloading();

      return responseData;
    } catch (error) {
      console.error('Login request failed:', error);
      throw error;
    }
  },

  // Get user info from token (JWT is the single source of truth for user roles)
  getUserInfo: async () => {
    console.log('🔍 getUserInfo: Starting user info retrieval...');
    const token = sessionStorage.getItem('token');
    if (!token) {
      console.error('❌ getUserInfo: No token found in sessionStorage');
      throw new Error('No token found');
    }

    console.log('✅ getUserInfo: Token found in sessionStorage');
    console.log('🔑 getUserInfo: Token preview:', token.substring(0, 20) + '...');

    // Decode the JWT token to extract user information
    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      if (process.env.NODE_ENV === 'development') {
        console.log('Decoded token data:', tokenData);
      }

      const decodedUser = {
        user_id: tokenData.user_id || tokenData.sub || 'unknown',
        username: tokenData.username || tokenData.preferred_username || 'unknown',
        email: tokenData.email,
        name: tokenData.name,
        role: tokenData.role || 'CONSUMER', // Default to CONSUMER if no role specified
        access_tags: tokenData.access_tags || {}
      };

      if (process.env.NODE_ENV === 'development') {
        console.log('Extracted user info from token:', decodedUser);
      }

      // Check if we recently validated this token (within last 30 seconds)
      const lastValidation = sessionStorage.getItem('token_last_validated');
      const now = Date.now();
      const validationAge = lastValidation ? now - parseInt(lastValidation) : Infinity;

      if (validationAge < 30000) { // 30 seconds
        console.log('✅ getUserInfo: Token recently validated, skipping API call');
      } else {
        // Validate the token is still valid by making a simple API call
        // Use an endpoint that all authenticated users can access
        console.log('🔍 getUserInfo: Validating token with API call to /sources...');
        try {
          await api.get('/sources');
          console.log('✅ getUserInfo: Token validation successful');
          // Store validation timestamp
          sessionStorage.setItem('token_last_validated', now.toString());
        } catch (validationError) {
          console.error('💥 getUserInfo: Token validation failed!');
          console.error('💥 getUserInfo: Validation error:', validationError);
          console.error('💥 getUserInfo: Validation error status:', validationError.response?.status);
          console.error('💥 getUserInfo: Validation error data:', validationError.response?.data);

          // If we get a 401, the token is invalid
          if (validationError.response && validationError.response.status === 401) {
            console.error('🚫 getUserInfo: 401 error - token is invalid or expired');
            // Clear validation timestamp on failure
            sessionStorage.removeItem('token_last_validated');
            throw new Error('Invalid or expired token');
          }
          // For other errors (network issues, etc.), still return the user info
          // The backend will handle authorization for specific endpoints
          console.warn('⚠️ getUserInfo: Non-401 error during validation, continuing with user info');
        }
      }

      return decodedUser;
    } catch (tokenError) {
      console.error('Error decoding token:', tokenError);
      throw new Error('Invalid token format');
    }
  },

  // Schedule documentation preloading after authentication is established
  scheduleDocumentationPreloading: () => {
    // Start quickly to preload index.md and its images for immediate access
    setTimeout(() => {
      if (process.env.NODE_ENV === 'development') {
        console.log('📚 Starting enhanced documentation preloading with index.md priority...');
      }

      // Verify user is still authenticated before preloading
      const token = sessionStorage.getItem('token');
      if (!token) {
        if (process.env.NODE_ENV === 'development') {
          console.log('❌ Documentation preloading cancelled - no token found');
        }
        return;
      }

      // Start documentation preloading in background with index.md and images priority
      documentationService.preloadDocumentation().catch(error => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('📚 Documentation preloading failed (this is OK):', error.message);
        }
        // Don't throw or affect authentication - just log the failure
      });
    }, 200); // Start quickly after login (200ms to allow token to be stored in sessionStorage)
  },

  // Logout user (client-side only)
  logout: () => {
    sessionStorage.removeItem('token');
    // Clear documentation cache on logout
    documentationService.clearCache();

    // Clear ATR processor data
    if (window.atrMonitoringDataProcessor) {
      window.atrMonitoringDataProcessor.clearData();
    }

    // Clear video monitoring processor data if it exists
    if (window.videoMonitoringDataProcessor) {
      window.videoMonitoringDataProcessor.clearData();
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('🚪 authService.logout: Cleared token, documentation cache, and processor data');
    }
  },
};

export default authService;
